import React, {useCallback, useRef} from 'react';
import {Button, SidePopup} from '@tickchak/react-share/app';
import * as css from './AIMessageGenerator.css';
import {AIMessageGeneratorProps} from './utils/types';
import {useAIMessageGenerator} from './utils/useAIMessageGenerator';
import GeneratorChatMessage from './generatorChatMessage/GeneratorChatMessage';
import GeneratorSuggestions from './generatorSuggestions/GeneratorSuggestions';
import CustomScrollbar from '../customScrollbar/CustomScrollbar';
import useMobile from '../../hooks/useMobile';
import AiGenerate from '../../assets/icons/aiGenerate';

function AIMessageGenerator({
    show,
    title,
    placeholder,
    submit,
    approve,
    errorMessage,
    suggestions,
}: AIMessageGeneratorProps) {
    const {
        messages,
        promptText,
        setPromptText,
        draftLoading,
        approveLoading,
        error,
        reset,
        submitPrompt,
        handleApprove,
    } = useAIMessageGenerator({errorMessage, submit, approve, onClose: show.close});

    const isMobile = useMobile();
    const promptTextareaRef = useRef<HTMLTextAreaElement>(null);
    const scrollColumnRef = useRef<HTMLDivElement>(null);

    const handleSubmitPrompt = useCallback(async () => {
        if(!promptText.trim() || draftLoading) {
            return;
        }
        await submitPrompt();
        requestAnimationFrame(() => {
            const scrollEl = scrollColumnRef.current;
            if(!scrollEl) {
                return;
            }
            scrollEl.scrollTo({
                top: scrollEl.scrollHeight,
                behavior: 'smooth',
            });
        });
    }, [promptText, draftLoading, submitPrompt]);

    const resizePromptTextarea = useCallback((textarea?: HTMLTextAreaElement | null) => {
        const el = textarea ?? promptTextareaRef.current;
        if(!el) {
            return;
        }
        const maxHeight = css.PROMPT_TEXTAREA_MAX_HEIGHT_PX;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
        el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }, []);

    const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        resizePromptTextarea(e.target);
        setPromptText(e.target.value);
    };

    const handlePromptKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if(e.key !== 'Enter' || e.shiftKey) {
            return;
        }
        e.preventDefault();
        if(!promptText.trim() || draftLoading) {
            return;
        }
        void handleSubmitPrompt();
    }, [promptText, draftLoading, handleSubmitPrompt]);

    const disableSubmit = !promptText.trim() || draftLoading;
    const closePopup = () => {
        show.close().then(() => reset());
    };

    return (
        <>
            <css.panelGradientStyles />
            <SidePopup
                show={show}
                panelWidth="389px"
                ariaLabel={title}
                className="ai-message-generator-panel"
                data-cy="ai-message-generator"
            >
                <SidePopup.Header
                    title={title}
                    onClose={closePopup}
                    className="ai-message-generator-header"
                    data-cy="ai-message-generator-header"
                />
                <css.panelInner>
                    <css.scrollArea>
                        {!isMobile && (
                            <CustomScrollbar
                                contentRef={scrollColumnRef}
                                cssStyle="inset-inline-end: 3px;"
                                maxBottom={65}
                            />
                        )}
                        <css.scrollColumn ref={scrollColumnRef}>
                            {messages.length === 0 && (
                                <GeneratorSuggestions
                                    suggestions={suggestions}
                                    onSuggestionClick={setPromptText}
                                />
                            )}
                            {(messages.length > 0 || draftLoading) && (
                                <css.chatColumn>
                                    {messages.map((message, index) => (
                                        <GeneratorChatMessage
                                            key={index}
                                            message={message}
                                            index={index}
                                            messages={messages}
                                            draftLoading={draftLoading}
                                            approveLabel={approve.label}
                                            approveLoading={approveLoading}
                                            onApprove={handleApprove}
                                        />
                                    ))}
                                </css.chatColumn>
                            )}
                        </css.scrollColumn>
                        {error && (
                            <css.errorText role="alert" data-testid="ai-message-generator-error">
                                {error}
                            </css.errorText>
                        )}
                    </css.scrollArea>
                    <css.promptArea>
                        <css.promptTextarea
                            ref={promptTextareaRef}
                            value={promptText}
                            onChange={handlePromptChange}
                            onKeyDown={handlePromptKeyDown}
                            placeholder={placeholder}
                            data-testid="ai-message-generator-prompt"
                            rows={1}
                        />
                        <Button
                            disabled={disableSubmit}
                            onClick={() => void handleSubmitPrompt()}
                            data-testid="ai-message-generator-submit"
                            iconEnd={<AiGenerate />}
                            size="category"
                            variant="brand"
                            xhrState={submit.xhrState}
                        >
                            {submit.label}
                        </Button>
                    </css.promptArea>
                </css.panelInner>
            </SidePopup>
        </>
    );
}

export default AIMessageGenerator;
